import { useState } from "react";

/**
 * Custom hook that encapsulates the repeated formula-builder state pattern.
 * Used by: refFormula (Reference Value), cfFormula (Constraint), sifFormula (Single-Input).
 *
 * State:
 *   sources        — registered source questions [{questionId, referenceType}]
 *   formulaParts   — [{type:'source',sourceIndex:N}|{type:'operator',value:'+'}]
 *   building       — whether the builder UI is open
 *   sourceQuestionId / sourceQuestionType / sourceReferenceType / sourceItems / sourceHasGrandTotal
 *                  — picker state for the next source to register
 */
export function useFormulaBuilder() {
  const [sources, setSources] = useState([]);
  const [formulaParts, setFormulaParts] = useState([]);
  const [building, setBuilding] = useState(false);
  const [sourceQuestionId, setSourceQuestionId] = useState("");
  const [sourceQuestionType, setSourceQuestionType] = useState("");
  const [sourceReferenceType, setSourceReferenceType] = useState("total");
  const [sourceItems, setSourceItems] = useState([]);
  const [sourceHasGrandTotal, setSourceHasGrandTotal] = useState(false);

  /** Register the currently-picked source into the sources array. */
  const addSource = () => {
    if (!sourceQuestionId) return;
    setSources(prev => [...prev, { questionId: sourceQuestionId, referenceType: sourceReferenceType }]);
  };

  /** Append a source-reference token to the formula (used by cf/sif). */
  const addSourceToFormula = (idx) => {
    setFormulaParts(prev => [...prev, { type: 'source', sourceIndex: idx }]);
  };

  /** Append an operator token to the formula. */
  const addOperator = (op) => {
    setFormulaParts(prev => [...prev, { type: 'operator', value: op }]);
  };

  /**
   * Build a human-readable display string.
   * getLabel(source, index) is provided by the caller so it can reference
   * question state from the outer component.
   */
  const getDisplay = (getLabel) => {
    if (formulaParts.length === 0) return 'Build your formula below...';
    return formulaParts.map(part => {
      if (part.type === 'operator') {
        const d = part.value === '*' ? '×' : part.value === '-' ? '−' : part.value;
        return ` ${d} `;
      }
      const source = sources[part.sourceIndex];
      if (!source) return `[${part.sourceIndex}]`;
      return getLabel(source, part.sourceIndex);
    }).join('');
  };

  /** Clear formula parts and registered sources. */
  const clear = () => {
    setFormulaParts([]);
    setSources([]);
  };

  /** Full reset — clears everything including the source picker fields. */
  const reset = () => {
    setFormulaParts([]);
    setSources([]);
    setSourceQuestionId("");
    setSourceQuestionType("");
    setSourceReferenceType("total");
    setSourceItems([]);
    setSourceHasGrandTotal(false);
    setBuilding(false);
  };

  return {
    // State values
    sources,
    formulaParts,
    building,
    sourceQuestionId,
    sourceQuestionType,
    sourceReferenceType,
    sourceItems,
    sourceHasGrandTotal,
    // Setters (needed for direct JSX bindings)
    setSources,
    setFormulaParts,
    setBuilding,
    setSourceQuestionId,
    setSourceQuestionType,
    setSourceReferenceType,
    setSourceItems,
    setSourceHasGrandTotal,
    // Action helpers
    addSource,
    addSourceToFormula,
    addOperator,
    getDisplay,
    clear,
    reset,
  };
}

export default useFormulaBuilder;
